import * as React from 'react'
import { CesuContractBase } from '../../../services/json-schemas/base/cesu_contract'
import { CesuMileageReimbursementBase } from '../../../services/json-schemas/base/cesu_mileage_reimbursement'
import { SupplierBase } from '../../../services/json-schemas/base/supplier'
import { cesuContractChargeExemptions, cesuContractEstimateContributionPercentages, cesuContractI18nChargeExemption, cesuContractI18nManagementFeeUnitOfWork, cesuContractI18nRestWeekDayWorkedResolution, cesuContractManagementFeeUnitsOfWork, cesuContractOnDutyPresenceNightTimeOptions } from '../../../services/models/cesu-contracts.service'
import { cesuMileageReimbursementI18nApplyness } from '../../../services/models/cesu-mileage-reimbursements.service'
import { currencyFormat, currencySymbols } from '../../../services/utils/currency.service'
import { I18nService } from '../../../services/utils/i18n.service'
import { moment } from '../../../services/utils/moment.service'
import { useDebouncedValue } from '../../../hooks/use-debounced-value'
import { usePromise } from '../../../hooks/use-promise'
import CheckboxComponent from '../../shared/checkbox.component'
import CheckboxGroupComponent from '../../shared/checkbox-group.component'
import DropdownComponent from '../../shared/dropdown.component'
import { NoRightBorderStyledInputDecimal } from '../../shared/input-decimal.component'
import InputPrefixSuffixComponent from '../../shared/input-prefix-suffix.component'
import RadioGroupComponent from '../../shared/radio-group.component'
import { Select } from '../../shared/select.component'
import TooltipComponent from '../../shared/tooltip.component'

const i18nScoped = new I18nService(require('./cesu-priceable-form.component.yml'))

type CesuPricingLayoutArgs = {
  managementFeeField: React.ReactNode
  salaryField: React.ReactNode
  estimationBlock: JSX.Element | null
  additionalCompensationsField: React.ReactNode
  mileageReimbursementBlock: React.ReactNode | null
  applyIncreasesToManagementFeesField: React.ReactNode
  restWeekDayField: React.ReactNode
  highValueDayOffsField: React.ReactNode
  dayOffIncreasePercentageField: React.ReactNode
  weekendIncreaseField: React.ReactNode
  restWeekDayWorkedPaidField: React.ReactNode
  onDutyPresenceNightFields: React.ReactNode
}

type Props = {
  object: Pick<
    CesuContractBase, 'management_fee_with_taxes_cents' | 'management_fee_unit_of_work' | 'raw_employee_hourly_salary_cents' | 'employer_charge_percentage' |
    'charge_exemption' | 'estimated_monthly_hours' | 'apply_increases_to_management_fees' | 'rest_week_day' | 'high_value_day_offs' | 'day_off_increase_percentage' |
    'weekend_increase_percentage' | 'weekend_incluses_saturday' | 'rest_week_day_worked_paid' | 'on_duty_presence_night_start' | 'on_duty_presence_night_end' | 'default_interrupt_count'
  > & { cesu_mileage_reimbursement_attributes: Pick<CesuMileageReimbursementBase, 'id' | 'amount_cents' | 'applyness' | 'show_on_document'> & { _destroy?: boolean } }
  setObjectState: (partial: Partial<Props['object']>) => void
  market: Pick<SupplierBase, 'currency'>
  disableRawSalary?: boolean
  shortRadio?: boolean
  setEstimationErrors: React.Dispatch<React.SetStateAction<string[]>>
  lowWidth?: boolean
  children: (args: CesuPricingLayoutArgs) => React.ReactNode
}

export default function CesuPriceableFormComponent ({ object, setObjectState, market, disableRawSalary, shortRadio, lowWidth, setEstimationErrors, children }: Props) {
  const exemptionOptions = React.useMemo(() => cesuContractChargeExemptions.map(code => ({ value: code, label: cesuContractI18nChargeExemption(code) })), [])
  const additionalCompensationOptions = React.useMemo(() => ([{ value: 'mileage_reimbursement' as const, label: i18nScoped.t('mileage_reimbursement') }]), [])
  const mileageReimbursementApplynessOptions = React.useMemo(() => (['event', 'day'] as const).map(value => ({ value, label: cesuMileageReimbursementI18nApplyness(value, { capitalize: false }) })), [])
  const managementFeeUnitOfWorkOptions = React.useMemo(() => cesuContractManagementFeeUnitsOfWork.map(value => ({ value, label: cesuContractI18nManagementFeeUnitOfWork(value) })), [])
  const restWeekDayOptions = React.useMemo(() => ([1, 2, 3, 4, 5, 6, 0] as const).map(i => ({ value: i, label: moment().day(i).format('dddd') })), [])
  const highValueDayOffOptions = React.useMemo(() => ([
    { value: '05-01' as const, text: i18nScoped.t('high_value_day_offs.may_1st'), disabled: true },
    { value: '12-25' as const, text: i18nScoped.t('high_value_day_offs.december_25th'), disabled: !!disableRawSalary }
  ]), [disableRawSalary])

  const restWeekDayWorkedPaidChoices = React.useMemo(() => (
    ([true, false] as const).map(value => ({
      value,
      text: cesuContractI18nRestWeekDayWorkedResolution(value, shortRadio),
      tooltip: shortRadio ? null : i18nScoped.t(`rest_week_day_worked_paid.${value}_tooltip`),
      disabled: !!disableRawSalary
    }))
  ), [disableRawSalary, shortRadio])

  const debouncedMonthlyHours = useDebouncedValue(object.estimated_monthly_hours, 1000)
  const debouncedHourlySalaryCents = useDebouncedValue(object.raw_employee_hourly_salary_cents, 1000)

  const currencySymbol = currencySymbols[market.currency].symbol
  const mileageAttrs = object.cesu_mileage_reimbursement_attributes

  // A persisted CesuMileageReimbursement with amount_cents: 0 with disableRawSalary is a placeholder auto-created from the payslip edit
  // screen after the contract was validated (see Backoffice::CesuPayslipsController#resolve_placeholder_mileage_reimbursements!) and isn't
  // really "configured" — treat it as disabled here so it doesn't show up as an already-enabled additional compensation.
  const mileageEnabled = mileageAttrs != null && !mileageAttrs._destroy && (!disableRawSalary || mileageAttrs.amount_cents != 0)

  const [employeeChargePercentage, setEmployeeChargePercentage] = React.useState<number | null>(null)

  usePromise(async () => {
    if (!debouncedMonthlyHours || !debouncedHourlySalaryCents) {
      setObjectState({ employer_charge_percentage: 42 })
      setEmployeeChargePercentage(null)
      return
    }

    const response = await cesuContractEstimateContributionPercentages({
      estimated_monthly_hours: debouncedMonthlyHours,
      raw_employee_hourly_salary_cents: debouncedHourlySalaryCents,
      charge_exemption: object.charge_exemption
    })

    setObjectState({ employer_charge_percentage: response.employer_charge_percentage })
    setEmployeeChargePercentage(response.employee_charge_percentage)
  }, [debouncedMonthlyHours, debouncedHourlySalaryCents, object.charge_exemption, setObjectState], { setErrors: setEstimationErrors })

  const managementFeeField = React.useMemo(() => {
    const currentOption = managementFeeUnitOfWorkOptions.find(o => o.value === object.management_fee_unit_of_work)
    const managementFeeUnitOfWorkSuffix = <div id="cesu-management-fees-unit-of-work" style={{ display: 'flex', alignItems: 'center', gap: '0.3em' }}>
      { currencySymbol } /
      <DropdownComponent label={ <div className="dropdown-link">{ currentOption.label }</div> } dropdownBoxStyle={{ minWidth: 'unset', right: -15 }}>
        { managementFeeUnitOfWorkOptions.map(option => <div
          key={ option.value }
          onClick={ () => setObjectState({ management_fee_unit_of_work: option.value }) }
          children={ option.label } />) }
      </DropdownComponent>
    </div>

    return <>
      <label className="form-label">{ i18nScoped.t('management_fee_with_taxes_cents') }</label>
      <InputPrefixSuffixComponent suffix={ managementFeeUnitOfWorkSuffix }>
        <NoRightBorderStyledInputDecimal
          id="cesu-management-fees"
          precision={ 2 }
          placeholder={ i18nScoped.t('management_fee_with_taxes_cents') }
          value={ object.management_fee_with_taxes_cents === null ? null : object.management_fee_with_taxes_cents / 100 }
          onChange={ val => setObjectState({ management_fee_with_taxes_cents: val === null ? 0 : Math.round((val as number) * 100) }) } />
      </InputPrefixSuffixComponent>
    </>
  }, [currencySymbol, managementFeeUnitOfWorkOptions, object.management_fee_unit_of_work, object.management_fee_with_taxes_cents, setObjectState])

  const salaryField = React.useMemo(() => <>
    <TooltipComponent message={ i18nScoped.t('raw_employee_hourly_salary_cents_tooltip') }>
      <label className="form-label">{ i18nScoped.t('raw_employee_hourly_salary_cents') } </label>
    </TooltipComponent>
    <InputPrefixSuffixComponent suffix="€/h">
      <NoRightBorderStyledInputDecimal
        id="cesu-salary"
        precision={ 2 }
        placeholder={ i18nScoped.t('raw_employee_hourly_salary_cents') }
        value={ object.raw_employee_hourly_salary_cents == null ? null : object.raw_employee_hourly_salary_cents / 100 }
        disabled={ disableRawSalary }
        onChange={ val => setObjectState({ raw_employee_hourly_salary_cents: val === null ? 0 : Math.round((val as number) * 100) }) } />
    </InputPrefixSuffixComponent>
  </>, [disableRawSalary, object.raw_employee_hourly_salary_cents, setObjectState])

  const estimationBlock = React.useMemo(() => {
    if (typeof object.management_fee_with_taxes_cents !== 'number' || typeof object.raw_employee_hourly_salary_cents !== 'number') return null

    return <div className="boxPod u-mtn" style={{ padding: '1em' }}>
      <div className="u-borderB u-mbs u-pbs" style={{ fontSize: '95%' }}>
        ⇄ { object.management_fee_unit_of_work === 'event'
          ? i18nScoped.tFragment('estimated_loaded_salary', {
              rate: <b>{ object.employer_charge_percentage }</b>,
              price: <b>{ currencyFormat(market.currency, (Math.round((1 + object.employer_charge_percentage / 100) * object.raw_employee_hourly_salary_cents) / 100).toFixed(2)) }</b>
            })
          : i18nScoped.tFragment('estimated_customer_price', {
              rate: <b>{ object.employer_charge_percentage }</b>,
              price: <b>{ currencyFormat(market.currency, (Math.round((1 + object.employer_charge_percentage / 100) * object.raw_employee_hourly_salary_cents + object.management_fee_with_taxes_cents) / 100).toFixed(2)) }</b>
            }) }

        { employeeChargePercentage !== null && <div>
          ⇄ { i18nScoped.tFragment('employee_net_salary', {
            rate: <b>{ employeeChargePercentage }</b>,
            price: <b>{ currencyFormat(market.currency, ((1 - employeeChargePercentage / 100) * object.raw_employee_hourly_salary_cents / 100).toFixed(2)) }</b>
          }) }
        </div> }
      </div>

      <div className="grid">
        <div className="grid-1-2 u-mbs">
          <label htmlFor="js-cesu-monthly-hours" className="form-label">{ i18nScoped.t('monthly_hours') }</label>
          <input
            id="js-cesu-monthly-hours"
            className="input"
            type="number"
            min={ 0 }
            value={ object.estimated_monthly_hours ?? '' }
            onChange={ e => setObjectState({ estimated_monthly_hours: e.target.value ? parseInt(e.target.value, 10) : null }) } />
        </div>

        <div className="grid-1-2 u-mbs">
          <label className="form-label">{ i18nScoped.t('exemption_code') }</label>
          <Select
            id="js-cesu-charge-exemption"
            value={ exemptionOptions.find(o => o.value === object.charge_exemption) ?? null }
            options={ exemptionOptions }
            isSearchable={ false }
            onChange={ opt => setObjectState({ charge_exemption: opt?.value ?? '02' }) } />
        </div>
      </div>

      <p className="txtSmall">
        <em className="icon-info u-mrs" />
        { i18nScoped.tFragment('warning', { link: <a href="https://www.cesu.urssaf.fr/decla/index.html?page=page_estimer&LANG=FR" target="_blank" rel="noopener noreferrer">{ i18nScoped.t('simulator') }</a> }) }
      </p>
    </div>
  }, [employeeChargePercentage, exemptionOptions, market.currency, object.charge_exemption, object.employer_charge_percentage, object.estimated_monthly_hours, object.management_fee_unit_of_work, object.management_fee_with_taxes_cents, object.raw_employee_hourly_salary_cents, setObjectState])

  const additionalCompensationsField = React.useMemo(() => <>
    <label className="form-label">{ i18nScoped.t('additional_compensations') }</label>
    <Select
      id="cesu-contract-additional-compensations"
      value={ mileageEnabled ? additionalCompensationOptions : [] }
      options={ additionalCompensationOptions }
      isMulti
      isSearchable={ false }
      isDisabled={ disableRawSalary }
      onChange={ opts => {
        const mileageSelected = (opts ?? []).some(o => o.value === 'mileage_reimbursement')

        if (mileageSelected && !mileageEnabled) {
          setObjectState({
            cesu_mileage_reimbursement_attributes: mileageAttrs?._destroy
              ? { ...mileageAttrs, _destroy: undefined }
              : { id: null, amount_cents: null, applyness: 'event', show_on_document: false }
          })
        } else if (!mileageSelected && mileageEnabled) {
          setObjectState({ cesu_mileage_reimbursement_attributes: mileageAttrs?.id ? { ...mileageAttrs, _destroy: true } : null })
        }
      } } />
  </>, [additionalCompensationOptions, disableRawSalary, mileageAttrs, mileageEnabled, setObjectState])

  const mileageReimbursementBlock = React.useMemo(() => {
    if (!mileageEnabled) return null

    const currentOption = mileageReimbursementApplynessOptions.find(o => o.value === mileageAttrs.applyness)
    const mileageReimbursementApplynessSuffix = <div id="cesu-contract-mileage-reimbursement-applyness" style={{ display: 'flex', alignItems: 'center', gap: '0.3em' }}>
      { currencySymbol } /
      { disableRawSalary
        ? <div>{ currentOption.label }</div>
        : <DropdownComponent label={ <div className="dropdown-link">{ currentOption.label }</div> } dropdownBoxStyle={{ minWidth: 'unset', right: -15 }}>
            { mileageReimbursementApplynessOptions.map(option => <div
              key={ option.value }
              onClick={ () => setObjectState({ cesu_mileage_reimbursement_attributes: { ...mileageAttrs, applyness: option.value } }) }
              children={ option.label } />) }
          </DropdownComponent> }
    </div>

    return <div className="grid">
      <div className="grid-4-4"><label className="form-label">{ i18nScoped.t('mileage_reimbursement_amount_cents') }</label></div>

      <div className="grid-1-2">
        <InputPrefixSuffixComponent suffix={ mileageReimbursementApplynessSuffix }>
          <NoRightBorderStyledInputDecimal
            id="cesu-contract-mileage-reimbursement-amount"
            precision={ 2 }
            placeholder={ i18nScoped.t('mileage_reimbursement_amount_cents') }
            value={ mileageAttrs.amount_cents === null ? null : mileageAttrs.amount_cents / 100 }
            disabled={ disableRawSalary }
            onChange={ val => setObjectState({ cesu_mileage_reimbursement_attributes: { ...mileageAttrs, amount_cents: val === null ? null : Math.round((val as number) * 100) } }) } />
        </InputPrefixSuffixComponent>
      </div>

      <div className="grid-1-2">
        <TooltipComponent message={ i18nScoped.t('mileage_reimbursement_show_on_document_tooltip') }>
          <CheckboxComponent
            id="cesu-contract-mileage-reimbursement-show-on-document"
            checked={ mileageAttrs.show_on_document }
            label={ i18nScoped.t('mileage_reimbursement_show_on_document') }
            disabled={ disableRawSalary }
            onChange={ val => setObjectState({ cesu_mileage_reimbursement_attributes: { ...mileageAttrs, show_on_document: val } }) } />
        </TooltipComponent>
      </div>
    </div>
  }, [currencySymbol, disableRawSalary, mileageAttrs, mileageEnabled, mileageReimbursementApplynessOptions, setObjectState])

  const applyIncreasesToManagementFeesField = React.useMemo(() => (
    <CheckboxComponent
      id="apply_increases_to_management_fees"
      label={ i18nScoped.t('apply_increases_to_management_fees') }
      checked={ object.apply_increases_to_management_fees }
      onChange={ val => setObjectState({ apply_increases_to_management_fees: val }) } />
  ), [object.apply_increases_to_management_fees, setObjectState])

  const restWeekDayField = React.useMemo(() => <>
    <label className="form-label">{ i18nScoped.t('rest_week_day') }</label>
    <Select
      id="cesu-contract-rest-week-day"
      value={ restWeekDayOptions.find(o => o.value === object.rest_week_day) ?? null }
      options={ restWeekDayOptions }
      isSearchable={ false }
      isDisabled={ disableRawSalary }
      onChange={ opt => setObjectState({ rest_week_day: opt?.value ?? 0 }) } />
  </>, [disableRawSalary, object.rest_week_day, restWeekDayOptions, setObjectState])

  const highValueDayOffsField = React.useMemo(() => <>
    <label className="form-label">{ i18nScoped.t('high_value_day_offs.label') }</label>
    <CheckboxGroupComponent
      name="cesu-contract-high-value-day-offs"
      innerClassName='u-mbn'
      choices={ highValueDayOffOptions }
      values={ object.high_value_day_offs }
      onChange={ values => setObjectState({ high_value_day_offs: values }) } />
  </>, [highValueDayOffOptions, object.high_value_day_offs, setObjectState])

  const dayOffIncreasePercentageField = React.useMemo(() => <>
    <label className="form-label">{ i18nScoped.t('day_off_increase_percentage') }</label>
    <InputPrefixSuffixComponent suffix="%">
      <input
        className="input"
        type="number"
        min={ 0 }
        value={ object.day_off_increase_percentage }
        disabled={ disableRawSalary }
        onChange={ e => setObjectState({ day_off_increase_percentage: parseInt(e.target.value, 10) || 0 }) } />
    </InputPrefixSuffixComponent>
  </>, [disableRawSalary, object.day_off_increase_percentage, setObjectState])

  const weekendIncreaseField = React.useMemo(() => <>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', justifyContent: 'space-between' }}>
      <label className="form-label">{ i18nScoped.t('weekend_increase_percentage') }</label>

      <CheckboxComponent
        id="cesu-contract-weekend-incluses-saturday"
        label={ i18nScoped.t('weekend_incluses_saturday') }
        checked={ object.weekend_incluses_saturday }
        disabled={ disableRawSalary }
        onChange={ val => setObjectState({ weekend_incluses_saturday: val }) } />
    </div>

    <InputPrefixSuffixComponent suffix="%">
      <input
        className="input"
        type="number"
        min={ 0 }
        value={ object.weekend_increase_percentage }
        disabled={ disableRawSalary }
        onChange={ e => setObjectState({ weekend_increase_percentage: parseInt(e.target.value, 10) || 0 }) } />
    </InputPrefixSuffixComponent>
  </>, [disableRawSalary, object.weekend_incluses_saturday, object.weekend_increase_percentage, setObjectState])

  const restWeekDayWorkedPaidField = React.useMemo(() => <>
    <label className="form-label">{ i18nScoped.t('rest_week_day_worked_paid.label') }</label>
    <RadioGroupComponent
      name="cesu-contract-rest-week-day-worked-paid"
      value={ object.rest_week_day_worked_paid }
      choices={ restWeekDayWorkedPaidChoices }
      onChange={ val => setObjectState({ rest_week_day_worked_paid: val }) } />
  </>, [object.rest_week_day_worked_paid, restWeekDayWorkedPaidChoices, setObjectState])

  const onDutyPresenceNightFields = React.useMemo(() => {
    const { startOptions, endOptions } = cesuContractOnDutyPresenceNightTimeOptions(object.on_duty_presence_night_start, object.on_duty_presence_night_end)

    return <>
      <label className="form-label">{ i18nScoped.t('on_duty_presence_night_title') }</label>
      <div className="grid">
        <div className={ lowWidth ? 'grid-1-5' : 'grid-1-3' }>
          <label>{ i18nScoped.t('on_duty_presence_night_start') }</label>
          <Select
            id="cesu-contract-on-duty-presence-night-start"
            value={{ value: object.on_duty_presence_night_start }}
            options={ startOptions.map(value => ({ value })) }
            getOptionLabel={ o => o.value }
            isSearchable={ false }
            isDisabled={ disableRawSalary }
            onChange={ opt => setObjectState({ on_duty_presence_night_start: opt.value }) } />
        </div>

        <div className={ lowWidth ? 'grid-1-5' : 'grid-1-3' }>
          <label>{ i18nScoped.t('on_duty_presence_night_end') }</label>
          <Select
            id="cesu-contract-on-duty-presence-night-end"
            value={{ value: object.on_duty_presence_night_end }}
            options={ endOptions.map(value => ({ value })) }
            getOptionLabel={ o => o.value }
            isSearchable={ false }
            isDisabled={ disableRawSalary }
            onChange={ opt => setObjectState({ on_duty_presence_night_end: opt.value }) } />
        </div>

        <div className={ lowWidth ? 'grid-3-5' : 'grid-1-3' }>
          <TooltipComponent message={ i18nScoped.t('default_interrupt_count_tooltip') }>
            <label>{ i18nScoped.t('default_interrupt_count') }</label>
          </TooltipComponent>
          <input
            id="cesu-contract-default-interrupt-count"
            className="input"
            type="number"
            min={ 0 }
            value={ object.default_interrupt_count ?? '' }
            disabled={ disableRawSalary }
            onChange={ e => setObjectState({ default_interrupt_count: e.target.value ? parseInt(e.target.value, 10) : null }) } />
        </div>
      </div>
    </>
  }, [disableRawSalary, lowWidth, object.default_interrupt_count, object.on_duty_presence_night_end, object.on_duty_presence_night_start, setObjectState])

  return <>{ children({ managementFeeField, salaryField, estimationBlock, additionalCompensationsField, mileageReimbursementBlock, applyIncreasesToManagementFeesField, restWeekDayField, highValueDayOffsField, dayOffIncreasePercentageField, weekendIncreaseField, restWeekDayWorkedPaidField, onDutyPresenceNightFields }) }</>
}